import { Button, Group } from '@mantine/core';
import BreakView from './BreakView';
import PageView from './PageView';

const PaginationBoxView = ({
  initialPage,
  forcePage,
  pageCount,
  pageRangeDisplayed,
  onPageChange,
  previousLabel,
  nextLabel,
  breakLabel,
  marginPagesDisplayed,
}) => {
  let selected;
  if (initialPage) {
    selected = initialPage;
  } else if (forcePage) {
    selected = forcePage;
  } else {
    selected = 0;
  }

  const callCallback = (selectedItem) => {
    if (typeof onPageChange === 'function') {
      onPageChange({ selected: selectedItem });
    }
  };

  const handlePageSelected = (newSelected, evt) => {
    evt.preventDefault ? evt.preventDefault() : (evt.returnValue = false);
    callCallback(newSelected);
  };

  const handlePreviousPage = (evt) => {
    evt.preventDefault ? evt.preventDefault() : (evt.returnValue = false);
    if (selected > 1) {
      handlePageSelected(selected - 1, evt);
    }
  };

  const handleNextPage = (evt) => {
    evt.preventDefault ? evt.preventDefault() : (evt.returnValue = false);
    if (selected < pageCount) {
      handlePageSelected(selected + 1, evt);
    }
  };

  const getForwardJump = () => {
    const forwardJump = selected + pageRangeDisplayed;
    return forwardJump >= pageCount ? pageCount - 1 : forwardJump;
  };

  const getBackwardJump = () => {
    const backwardJump = selected - pageRangeDisplayed;
    return backwardJump < 0 ? 0 : backwardJump;
  };

  const handleBreakClick = (index, evt) => {
    evt.preventDefault ? evt.preventDefault() : (evt.returnValue = false);

    handlePageSelected(selected < index ? getForwardJump() : getBackwardJump(), evt);
  };

  const getPageElement = (index) => {
    return (
      <PageView
        key={index}
        pageSelectedHandler={(e) => handlePageSelected(index, e)}
        selected={selected === index}
        page={index}
      />
    );
  };

  const pagination = () => {
    const items = [];

    if (pageCount <= pageRangeDisplayed) {
      for (let index = 1; index <= pageCount; index++) {
        items.push(getPageElement(index));
      }
    } else {
      let leftSide = pageRangeDisplayed / 2;
      let rightSide = pageRangeDisplayed - leftSide;

      // If the selected page index is on the default right side of the pagination,
      // we consider that the new right side is made up of it (= only one break element).
      // If the selected page index is on the default left side of the pagination,
      // we consider that the new left side is made up of it (= only one break element).
      if (selected > pageCount - pageRangeDisplayed / 2) {
        rightSide = pageCount - selected;
        leftSide = pageRangeDisplayed - rightSide;
      } else if (selected <= pageRangeDisplayed / 2) {
        leftSide = selected;
        rightSide = pageRangeDisplayed - leftSide;
      }

      let breakView;
      let createPageView = (index) => getPageElement(index);

      for (let page = 1; page <= pageCount; page++) {
        // If the page index is lower than the margin defined,
        // the page has to be displayed on the left side of
        // the pagination.
        if (page <= marginPagesDisplayed) {
          items.push(createPageView(page));
          continue;
        }

        // If the page index is greater than the page count
        // minus the margin defined, the page has to be
        // displayed on the right side of the pagination.
        if (page > pageCount - marginPagesDisplayed) {
          items.push(createPageView(page));
          continue;
        }

        // If the page index is near the selected page index
        // and inside the defined range (pageRangeDisplayed)
        // we have to display it (it will create the center
        // part of the pagination).
        if (page >= selected - leftSide && page <= selected + rightSide) {
          items.push(createPageView(page));
          continue;
        }

        // If the page index doesn't meet any of the conditions above,
        // we check if the last item of the current "items" array
        // is a break element. If not, we add a break element, else,
        // we do nothing (because we don't want to display the page).
        if (breakLabel && items[items.length - 1] !== breakView) {
          breakView = <BreakView key={page} breakLabel={breakLabel} breakHandler={handleBreakClick.bind(null, page)} />;
          items.push(breakView);
        }
      }
    }

    return items;
  };

  let prevButton = '';
  if (selected > 1) {
    prevButton = (
      <Button
        variant="outline"
        size="sm"
        tabIndex="0"
        role="button"
        onClick={handlePreviousPage}
        aria-label="previous page"
      >
        {previousLabel}
      </Button>
    );
  }

  let nextButton = '';
  if (selected < pageCount) {
    nextButton = (
      <Button variant="outline" size="sm" tabIndex="0" role="button" onClick={handleNextPage} aria-label="next page">
        {nextLabel}
      </Button>
    );
  }

  return (
    <Group className="break" gap="xs">
      {prevButton}
      {pagination()}
      {nextButton}
    </Group>
  );
};

export default PaginationBoxView;
